import React from 'react'
import {CompatRoute, Navigate, useLocation} from 'react-router-dom-v5-compat'

import {useAuth} from '../../stores/AuthStore'
import {useReleaseFlags} from '../../hooks/useReleaseFlags'
import {FeatureName} from '../../types'
import {PageTemplate} from '../../templates/PageTemplate/PageTemplate'
import {LoadingState} from '../LoadingState/LoadingState'
import {HOME_PATH, LOGIN_PATH} from '../../navigation'

type Props = React.ComponentProps<typeof CompatRoute> & {
  clientAuthorization?: FeatureName
  releaseRender?: {
    releaseFlag: FeatureName
    render: () => React.ReactNode
  }
}

export const AuthRoute: React.FC<React.PropsWithChildren<Props>> = ({
  clientAuthorization,
  releaseRender,
  compat,
  ...props
}) => {
  const location = useLocation()
  const {authenticationStatus, useIdpAuthentication, client, person} = useAuth()
  const {getIsReleasedToUser} = useReleaseFlags()

  if (authenticationStatus === 'unauthenticated') {
    return (
      <Navigate
        to={LOGIN_PATH}
        state={{
          from: location,
        }}
        replace
      />
    )
  }

  // User is using Auth0 and we're in a incompleted auth state
  // when a new token is being exchanged we want to unmount all the normal route content so that when it reloads it refetches with the new token
  if (
    useIdpAuthentication &&
    (authenticationStatus === 'idp_authenticated' ||
      authenticationStatus === 'pending')
  ) {
    return (
      <PageTemplate width={['100%']} maxWidth={['1030px']} borderX={['0px']}>
        <LoadingState />
      </PageTemplate>
    )
  }

  // Auth has not fully resolved yet: the required client/person are still null
  // (the initial/pending window, or a rehydrated non-authenticated status). The
  // protected views call useRequiredAuth(), which throws when client/person are
  // null — on a fresh deep-link load that throw crashed the page (AB#21202).
  // Render a loading state until auth settles; it then resolves to authenticated
  // (route renders) or unauthenticated (redirected above), so this is transient.
  // Must come before the clientAuthorization check below, whose
  // getIsReleasedToUser returns false on a null releaseFlags and would wrongly
  // redirect to /home during the null window.
  if (!client || !person) {
    return (
      <PageTemplate width={['100%']} maxWidth={['1030px']} borderX={['0px']}>
        <LoadingState />
      </PageTemplate>
    )
  }

  if (clientAuthorization) {
    const isAuthorized = getIsReleasedToUser(clientAuthorization)

    if (!isAuthorized) {
      return <Navigate to={HOME_PATH} replace />
    }
  }

  // Used to conditionally render a component based on a release flag.
  // To be used for temporary release flags only to ensure large view
  // changes can be verified in production first and possibly canary release
  const additionalProps: {
    render?: Props['render']
  } = {}

  if (releaseRender) {
    const isReleased = getIsReleasedToUser(releaseRender.releaseFlag)

    if (isReleased) {
      additionalProps.render = releaseRender.render
    }
  }

  return <CompatRoute {...props} {...additionalProps} />
}
